/**
 * Service Map Controller (TypeScript)
 * Manages service map discovery and visualization workflow
 *
 * Responsibilities:
 * - Coordinate service map generation from ii_agent
 * - Manage visualization lifecycle with ServiceMapVisualizer
 * - Update UI progress indicators and stats
 * - Handle errors and user notifications
 */

import { safeFeatherReplace } from '../utils/feather-helper.js';

/**
 * II Agent interface for service map generation
 */
interface IIAgent {
    generateServiceMap(serviceName: string): Promise<ServiceMapData>;
    generateServiceMapLive(serviceName: string): Promise<ServiceMapGenerationResponse>;
}

/**
 * Graph update types for incremental visualization
 */
interface GraphUpdate {
    action: string;
    node_data?: GraphNode;
    edge_data?: GraphEdge;
}

interface GraphNode {
    id: string;
    ip: string;
    hostname: string;
    tier: string;
    tier_level: number;
    config_files: string[];
}

interface GraphEdge {
    from: string;
    to: string;
    connection_type: string;
    port?: number;
}

interface ServiceMapGenerationStep {
    timestamp: string;
    log_message: string;
    log_level: string;
    graph_update?: GraphUpdate;
}

interface ServiceMapGenerationResponse {
    service_name: string;
    generation_steps: ServiceMapGenerationStep[];
    final_service_map: ServiceMapData;
}

/**
 * UI elements for controller interaction
 */
interface UIElements {
    statusBar: HTMLElement | null;
    statusText: HTMLElement | null;
    progressText: HTMLElement | null;
    progressBar: HTMLElement | null;
    runBtn: HTMLButtonElement | null;
    statsDiv: HTMLElement | null;
}

/**
 * Statistics to display in UI
 */
interface ServiceMapStats {
    nodes: number;
    edges: number;
    time: string;
    tiers: number;
}

/**
 * Service Map Controller
 * Orchestrates the service discovery and visualization process
 */
export class ServiceMapController {
    private iiAgent: IIAgent;
    private visualizer: ServiceMapVisualizer | null;
    private currentServiceMap: ServiceMapData | null;
    private static readonly STORAGE_KEY = 'asd_service_maps';
    private currentTab: 'topology' | 'active-models' | 'analysis' = 'topology';

    /** Service map names that belong in the Topology tab */
    private static readonly TOPOLOGY_MAP_NAMES = [
        'Consolidated Platform Target State'
    ];

    /** Service map names that belong in the Analysis tab */
    private static readonly ANALYSIS_MAP_NAMES = [
        'Platform Overlap Analysis'
    ];

    /**
     * Create a new service map controller
     * @param iiAgent - II Agent instance for service map generation
     */
    constructor(iiAgent: IIAgent) {
        this.iiAgent = iiAgent;
        this.visualizer = null;
        this.currentServiceMap = null;
    }

    /**
     * Check if a service map belongs in the Topology tab
     * @param serviceName - Name of the service map
     * @returns true if it's a topology-type map
     */
    private isTopologyMap(serviceName: string): boolean {
        return ServiceMapController.TOPOLOGY_MAP_NAMES.includes(serviceName);
    }

    /**
     * Check if a service map belongs in the Analysis tab
     * @param serviceName - Name of the service map
     * @returns true if it's an analysis-type map
     */
    private isAnalysisMap(serviceName: string): boolean {
        return ServiceMapController.ANALYSIS_MAP_NAMES.includes(serviceName);
    }

    /**
     * Switch between Topology, Active Service Models, and Analysis tabs
     * @param tab - Tab to switch to ('topology', 'active-models', or 'analysis')
     */
    switchServiceModelsTab(tab: 'topology' | 'active-models' | 'analysis'): void {
        this.currentTab = tab;

        // Get all tab elements
        const topologyTab = document.getElementById('topology-tab');
        const activeModelsTab = document.getElementById('active-models-tab');
        const analysisTab = document.getElementById('analysis-tab');
        const topologyContent = document.getElementById('topology-tab-content');
        const activeModelsContent = document.getElementById('active-models-tab-content');
        const analysisContent = document.getElementById('analysis-tab-content');

        // Reset all tabs to inactive state
        const allTabs = [topologyTab, activeModelsTab, analysisTab];
        const allContents = [topologyContent, activeModelsContent, analysisContent];

        allTabs.forEach(t => {
            t?.classList.remove('border-blue-600', 'text-blue-600');
            t?.classList.add('border-transparent', 'text-gray-500');
        });
        allContents.forEach(c => c?.classList.add('hidden'));

        // Activate selected tab
        if (tab === 'topology') {
            topologyTab?.classList.add('border-blue-600', 'text-blue-600');
            topologyTab?.classList.remove('border-transparent', 'text-gray-500');
            topologyContent?.classList.remove('hidden');
        } else if (tab === 'active-models') {
            activeModelsTab?.classList.add('border-blue-600', 'text-blue-600');
            activeModelsTab?.classList.remove('border-transparent', 'text-gray-500');
            activeModelsContent?.classList.remove('hidden');
        } else if (tab === 'analysis') {
            analysisTab?.classList.add('border-blue-600', 'text-blue-600');
            analysisTab?.classList.remove('border-transparent', 'text-gray-500');
            analysisContent?.classList.remove('hidden');
        }

        // Refresh feather icons after tab switch
        safeFeatherReplace();
    }

    /**
     * Store a service map in vector_db (single source of truth) and localStorage
     * Sprint 21: Consolidated to use vector_db instead of service_mapper
     * @param serviceMap - Service map to store
     */
    private async storeServiceMap(serviceMap: ServiceMapData): Promise<void> {
        try {
            // 1. Save to vector_db (single source of truth - ADR-002)
            console.log('💾 Saving service map to vector_db:', serviceMap.service_name);

            const canisterClient = (window as any).canisterClient;
            if (canisterClient) {
                try {
                    // Convert to vector_db ServiceMapStorageRequest format
                    const storageRequest = this.toVectorDbFormat(serviceMap);
                    console.log('📦 Converted to vector_db format:', storageRequest);

                    const result = await canisterClient.callCanister(
                        'vector_db',
                        'store_service_map',
                        [storageRequest]
                    );

                    console.log('📊 store_service_map result:', result);

                    // Proxy returns { data: Result, raw: string }
                    const actualResult = result?.data || result;
                    console.log('📊 actualResult:', actualResult);

                    // vector_db returns ServiceMapStorageResponse with success field
                    if (actualResult && actualResult.success === true) {
                        console.log('✅ Service map saved to vector_db:', serviceMap.service_name);
                        console.log(`   Applications: ${actualResult.applications_created}, Relationships: ${actualResult.relationships_created}`);

                        if (typeof (window as any).showToastNotification === 'function') {
                            (window as any).showToastNotification(
                                `Service map "${serviceMap.service_name}" saved (${actualResult.applications_created} apps, ${actualResult.relationships_created} relationships)`,
                                'success'
                            );
                        }
                    } else if (actualResult && actualResult.success === false) {
                        throw new Error(actualResult.message || 'Unknown error');
                    } else {
                        console.error('❌ Unexpected result format:', result);
                        throw new Error('Failed to save to vector_db: unexpected result format');
                    }
                } catch (blockchainError) {
                    console.error('❌ Failed to save to vector_db:', blockchainError);

                    if (typeof (window as any).showToastNotification === 'function') {
                        (window as any).showToastNotification(
                            'Failed to save to vector_db (saved locally only): ' + (blockchainError as Error).message,
                            'warning'
                        );
                    }
                }
            } else {
                console.warn('⚠️ CanisterClient not available, storing locally only');
            }

            // 2. Also cache in localStorage for fast access (fallback)
            this.storeServiceMapLocal(serviceMap);

        } catch (error) {
            console.error('❌ Failed to store service map:', error);
            if (typeof (window as any).showToastNotification === 'function') {
                (window as any).showToastNotification(
                    'Failed to save service map: ' + (error as Error).message,
                    'error'
                );
            }
        }
    }

    /**
     * Convert ServiceMapData to vector_db ServiceMapStorageRequest format
     * Sprint 21: Consolidated storage format
     *
     * vector_db expects:
     * - name: string
     * - tiers: ServiceTier[] (with tier_name, tier_level, devices[])
     * - metadata: optional key-value pairs
     */
    private toVectorDbFormat(serviceMap: ServiceMapData): any {
        // Build metadata from discovery info
        const metadata: [string, string][] = [
            ['discovered_at', String(serviceMap.discovered_at)],
            ['total_devices', String(serviceMap.total_devices)],
            ['source', 'discovery'],
        ];

        // Add discovery logs count if available
        if (serviceMap.discovery_logs && serviceMap.discovery_logs.length > 0) {
            metadata.push(['discovery_logs_count', String(serviceMap.discovery_logs.length)]);
        }

        // Convert tiers to vector_db format
        const tiers = serviceMap.tiers.map(tier => ({
            tier_name: tier.tier_name,
            tier_level: tier.tier_level,
            devices: tier.devices.map(device => ({
                device_id: device.device_id,
                hostname: device.hostname,
                ip_address: device.ip_address,
                role: device.role,
                config_files: device.config_files || []
            }))
        }));

        return {
            name: serviceMap.service_name,
            tiers: tiers,
            metadata: metadata
        };
    }

    /**
     * Store service map in localStorage only (cache)
     * @param serviceMap - Service map to store locally
     */
    private storeServiceMapLocal(serviceMap: ServiceMapData): void {
        try {
            const stored = this.getStoredServiceMaps();

            // Check if service map with same name already exists
            const existingIndex = stored.findIndex(sm => sm.service_name === serviceMap.service_name);

            if (existingIndex >= 0) {
                // Update existing service map
                stored[existingIndex] = serviceMap;
            } else {
                // Add new service map
                stored.push(serviceMap);
            }

            // Custom replacer to handle BigInt
            const replacer = (_key: string, value: any) => {
                if (typeof value === 'bigint') {
                    return value.toString();
                }
                return value;
            };

            localStorage.setItem(ServiceMapController.STORAGE_KEY, JSON.stringify(stored, replacer));
            console.log('✅ Service map cached locally:', serviceMap.service_name);

            // Update the UI display
            this.displayActiveServiceModels();
        } catch (error) {
            console.error('❌ Failed to cache service map locally:', error);
        }
    }

    /**
     * Get all stored service maps from localStorage
     * @returns Array of stored service maps
     */
    getStoredServiceMaps(): ServiceMapData[] {
        try {
            const stored = localStorage.getItem(ServiceMapController.STORAGE_KEY);
            if (!stored) return [];

            const maps = JSON.parse(stored);

            // Convert discovered_at string back to BigInt
            return maps.map((map: any) => ({
                ...map,
                discovered_at: typeof map.discovered_at === 'string'
                    ? BigInt(map.discovered_at)
                    : BigInt(map.discovered_at || Date.now() * 1_000_000)
            }));
        } catch (error) {
            console.error('❌ Failed to load service maps:', error);
            return [];
        }
    }

    /**
     * Delete a stored service map
     * @param serviceName - Name of the service map to delete
     */
    deleteServiceMap(serviceName: string): void {
        try {
            const stored = this.getStoredServiceMaps();
            const filtered = stored.filter(sm => sm.service_name !== serviceName);

            // Custom replacer to handle BigInt
            const replacer = (_key: string, value: any) => {
                if (typeof value === 'bigint') {
                    return value.toString();
                }
                return value;
            };

            localStorage.setItem(ServiceMapController.STORAGE_KEY, JSON.stringify(filtered, replacer));
            console.log('✅ Service map deleted:', serviceName);

            // Update the UI display
            this.displayActiveServiceModels();
        } catch (error) {
            console.error('❌ Failed to delete service map:', error);
        }
    }

    /**
     * Display active service models in the UI
     * Loads from blockchain first, then merges with localStorage
     * Sprint 32: Now populates Topology, Active Service Models, and Analysis tabs
     */
    async displayActiveServiceModels(): Promise<void> {
        console.log('📊 displayActiveServiceModels called');

        // Get containers - re-query each time in case DOM wasn't ready
        let topologyContainer = document.getElementById('topology-models-container');
        let serviceModelsContainer = document.getElementById('service-models-container');
        let analysisContainer = document.getElementById('analysis-models-container');

        if (!topologyContainer && !serviceModelsContainer && !analysisContainer) {
            console.warn('⚠️ No service model containers found in DOM, will retry after delay');
            // Retry after a short delay in case DOM isn't ready
            await new Promise(resolve => setTimeout(resolve, 100));
            topologyContainer = document.getElementById('topology-models-container');
            serviceModelsContainer = document.getElementById('service-models-container');
            analysisContainer = document.getElementById('analysis-models-container');

            if (!topologyContainer && !serviceModelsContainer && !analysisContainer) {
                console.error('❌ Service model containers still not found after retry');
                return;
            }
        }

        // Show loading state in all containers
        const loadingHtml = '<div class="text-center py-8"><i data-feather="loader" class="w-8 h-8 mx-auto animate-spin text-blue-600"></i><p class="mt-2 text-gray-600">Loading service maps from blockchain...</p></div>';
        if (topologyContainer) topologyContainer.innerHTML = loadingHtml;
        if (serviceModelsContainer) serviceModelsContainer.innerHTML = loadingHtml;
        if (analysisContainer) analysisContainer.innerHTML = loadingHtml;
        safeFeatherReplace();

        try {
            // Wait for canisterClient to be available
            let retries = 0;
            while (!(window as any).canisterClient && retries < 10) {
                console.log('⏳ Waiting for canisterClient...', retries);
                await new Promise(resolve => setTimeout(resolve, 200));
                retries++;
            }

            // Load from blockchain
            const blockchainMaps = await this.loadServiceMapsFromBlockchain();
            console.log('📊 Loaded from blockchain:', blockchainMaps.length, 'maps');

            // Merge with localStorage (blockchain is source of truth)
            const localMaps = this.getStoredServiceMaps();
            const allMaps = this.mergeServiceMaps(blockchainMaps, localMaps);
            console.log('📊 Total maps after merge:', allMaps.length);

            // Re-query containers in case they changed
            topologyContainer = document.getElementById('topology-models-container');
            serviceModelsContainer = document.getElementById('service-models-container');
            analysisContainer = document.getElementById('analysis-models-container');

            // Split maps into topology, analysis, and regular service models
            const topologyMaps = allMaps.filter(m => this.isTopologyMap(m.service_name));
            const analysisMaps = allMaps.filter(m => this.isAnalysisMap(m.service_name));
            const regularMaps = allMaps.filter(m => !this.isTopologyMap(m.service_name) && !this.isAnalysisMap(m.service_name));

            console.log('📊 Topology maps:', topologyMaps.length, 'Analysis maps:', analysisMaps.length, 'Regular maps:', regularMaps.length);

            // Render topology tab
            if (topologyContainer) {
                if (topologyMaps.length === 0) {
                    topologyContainer.innerHTML = `
                        <div class="text-center text-gray-500 py-8">
                            <i data-feather="git-merge" class="w-12 h-12 mx-auto mb-3 text-gray-400"></i>
                            <p class="text-lg font-medium">No topology maps discovered yet</p>
                            <p class="text-sm mt-2">Consolidated Platform Target State maps will appear here</p>
                        </div>
                    `;
                } else {
                    topologyContainer.innerHTML = this.renderServiceMapsList(topologyMaps);
                }
            }

            // Render active service models tab
            if (serviceModelsContainer) {
                if (regularMaps.length === 0) {
                    serviceModelsContainer.innerHTML = `
                        <div class="text-center text-gray-500 py-8">
                            <i data-feather="map" class="w-12 h-12 mx-auto mb-3 text-gray-400"></i>
                            <p class="text-lg font-medium">No service models discovered yet</p>
                            <p class="text-sm mt-2">Use "Service Map Generation" above to discover services</p>
                        </div>
                    `;
                } else {
                    serviceModelsContainer.innerHTML = this.renderServiceMapsList(regularMaps);
                }
            }

            // Render analysis tab
            if (analysisContainer) {
                if (analysisMaps.length === 0) {
                    analysisContainer.innerHTML = `
                        <div class="text-center text-gray-500 py-8">
                            <i data-feather="bar-chart-2" class="w-12 h-12 mx-auto mb-3 text-gray-400"></i>
                            <p class="text-lg font-medium">No analysis maps discovered yet</p>
                            <p class="text-sm mt-2">Platform Overlap Analysis maps will appear here</p>
                        </div>
                    `;
                } else {
                    analysisContainer.innerHTML = this.renderServiceMapsList(analysisMaps);
                }
            }

            // Replace feather icons
            safeFeatherReplace();

            // Ensure the current tab content is visible
            console.log('📊 Ensuring tab visibility for:', this.currentTab);
            this.switchServiceModelsTab(this.currentTab);

        } catch (error) {
            console.error('❌ Failed to load service maps:', error);
            // Fallback to localStorage only
            const localMaps = this.getStoredServiceMaps();
            const errorHtml = '<div class="text-yellow-600 p-4">Failed to load from blockchain. Showing local cache only.</div>';

            if (localMaps.length === 0) {
                if (topologyContainer) {
                    topologyContainer.innerHTML = `
                        <div class="text-center text-gray-500 py-8">
                            <i data-feather="git-merge" class="w-12 h-12 mx-auto mb-3 text-gray-400"></i>
                            <p class="text-lg font-medium">No topology maps discovered yet</p>
                        </div>
                    `;
                }
                if (serviceModelsContainer) {
                    serviceModelsContainer.innerHTML = `
                        <div class="text-center text-gray-500 py-8">
                            <i data-feather="map" class="w-12 h-12 mx-auto mb-3 text-gray-400"></i>
                            <p class="text-lg font-medium">No service models discovered yet</p>
                        </div>
                    `;
                }
                if (analysisContainer) {
                    analysisContainer.innerHTML = `
                        <div class="text-center text-gray-500 py-8">
                            <i data-feather="bar-chart-2" class="w-12 h-12 mx-auto mb-3 text-gray-400"></i>
                            <p class="text-lg font-medium">No analysis maps discovered yet</p>
                        </div>
                    `;
                }
            } else {
                if (topologyContainer) topologyContainer.innerHTML = errorHtml;
                if (serviceModelsContainer) serviceModelsContainer.innerHTML = errorHtml;
                if (analysisContainer) analysisContainer.innerHTML = errorHtml;
            }
            safeFeatherReplace();
        }
    }

    /**
     * Render a list of service maps as HTML
     * Sprint 32: Extracted to reusable method for both tabs
     * @param maps - Array of service maps to render
     * @returns HTML string for the service maps list
     */
    private renderServiceMapsList(maps: ServiceMapData[]): string {
        return maps.map((serviceMap) => {
            const date = new Date(Number(serviceMap.discovered_at) / 1_000_000);
            const formattedDate = date.toLocaleString();
            const logCount = serviceMap.discovery_logs?.length || 0;

            // Build tier names list with styling
            const tierNames = serviceMap.tiers
                .sort((a, b) => a.tier_level - b.tier_level)
                .map(t => t.tier_name)
                .join(' → ');

            return `
                <div class="service-model-card p-4 border-b last:border-b-0 hover:bg-gray-50 transition-colors">
                    <div class="flex justify-between items-start">
                        <div class="flex-1">
                            <div class="flex items-center gap-3 mb-2">
                                <i data-feather="map" class="w-5 h-5 text-blue-600"></i>
                                <h4 class="text-lg font-semibold text-gray-900">${serviceMap.service_name}</h4>
                                <span class="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">On-Chain</span>
                            </div>
                            <div class="text-sm text-gray-600 mb-2">
                                <span class="font-medium">Tiers:</span> ${tierNames}
                            </div>
                            <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 text-sm">
                                <div>
                                    <span class="text-gray-500">Devices:</span>
                                    <span class="ml-2 font-medium text-gray-900">${serviceMap.total_devices}</span>
                                </div>
                                <div>
                                    <span class="text-gray-500">Connections:</span>
                                    <span class="ml-2 font-medium text-gray-900">${serviceMap.connections.length}</span>
                                </div>
                                <div>
                                    <span class="text-gray-500">Logs:</span>
                                    <span class="ml-2 font-medium text-gray-900">${logCount}</span>
                                </div>
                                <div>
                                    <span class="text-gray-500">Discovered:</span>
                                    <span class="ml-2 font-medium text-gray-900">${formattedDate}</span>
                                </div>
                            </div>
                        </div>
                        <div class="flex gap-2 ml-4">
                            <button
                                onclick="window.serviceMapController.viewServiceMapLogs('${serviceMap.service_name}')"
                                class="px-3 py-1.5 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors text-sm flex items-center gap-1"
                                title="View discovery logs">
                                <i data-feather="file-text" class="w-4 h-4"></i>
                                Logs
                            </button>
                            <button
                                onclick="window.serviceMapController.loadServiceMap('${serviceMap.service_name}')"
                                class="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm flex items-center gap-1">
                                <i data-feather="eye" class="w-4 h-4"></i>
                                View
                            </button>
                            <button
                                onclick="window.serviceMapController.deleteServiceMap('${serviceMap.service_name}')"
                                class="px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm flex items-center gap-1">
                                <i data-feather="trash-2" class="w-4 h-4"></i>
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            `;
        }).join('');
    }

    /**
     * Load service maps from vector_db (single source of truth)
     * Sprint 21: Consolidated to use vector_db instead of service_mapper
     * Uses two-step approach: list names, then fetch each map individually
     * @returns Array of service maps from vector_db
     */
    private async loadServiceMapsFromBlockchain(): Promise<ServiceMapData[]> {
        try {
            const canisterClient = (window as any).canisterClient;
            if (!canisterClient) {
                console.warn('⚠️ CanisterClient not available');
                return [];
            }

            // Step 1: Get list of service map names
            const namesResult = await canisterClient.callCanister('vector_db', 'list_service_map_names', []);
            console.log('📊 list_service_map_names result:', namesResult);

            const serviceMapNames = namesResult?.data || namesResult || [];
            if (!Array.isArray(serviceMapNames) || serviceMapNames.length === 0) {
                console.log('📊 No service maps found');
                return [];
            }

            console.log('📊 Found service maps:', serviceMapNames);

            // Step 2: Fetch each service map by name
            const serviceMaps: ServiceMapData[] = [];
            for (const name of serviceMapNames) {
                try {
                    const mapResult = await canisterClient.callCanister('vector_db', 'get_service_map', [name]);
                    console.log(`📊 get_service_map("${name}") result:`, mapResult);

                    // Handle Option<ServiceMap> - could be [serviceMap] or serviceMap directly
                    const actualMap = mapResult?.data?.[0] || mapResult?.data || mapResult?.[0] || mapResult;

                    if (actualMap && actualMap.tiers) {
                        serviceMaps.push(this.fromVectorDbFormat(actualMap));
                    } else {
                        console.warn(`⚠️ Invalid service map format for "${name}":`, actualMap);
                    }
                } catch (mapError) {
                    console.error(`❌ Failed to load service map "${name}":`, mapError);
                }
            }

            console.log('📊 Loaded service maps:', serviceMaps.length);
            return serviceMaps;
        } catch (error) {
            console.error('❌ Failed to load from vector_db:', error);
            return [];
        }
    }

    /**
     * Convert vector_db ServiceMap format to ServiceMapData for visualization
     * Sprint 21: Reverse of toVectorDbFormat
     */
    private fromVectorDbFormat(serviceMap: any): ServiceMapData {
        // Extract metadata values
        const metadata = serviceMap.metadata || {};
        const discoveredAt = metadata.discovered_at
            ? BigInt(metadata.discovered_at)
            : BigInt(serviceMap.created_at || Date.now() * 1_000_000);

        // Count total devices from tiers
        const totalDevices = (serviceMap.tiers || []).reduce(
            (sum: number, tier: any) => sum + (tier.devices?.length || 0),
            0
        );

        // Build connections from tier relationships (inferred)
        const connections: ServiceConnection[] = [];
        const tiers = serviceMap.tiers || [];

        // Sort tiers by level
        const sortedTiers = [...tiers].sort((a: any, b: any) => a.tier_level - b.tier_level);

        // Create connections between adjacent tiers
        for (let i = 0; i < sortedTiers.length - 1; i++) {
            const sourceTier = sortedTiers[i];
            const targetTier = sortedTiers[i + 1];

            for (const sourceDevice of (sourceTier.devices || [])) {
                for (const targetDevice of (targetTier.devices || [])) {
                    connections.push({
                        from_device: sourceDevice.device_id,
                        to_device: targetDevice.device_id,
                        connection_type: this.inferConnectionType(sourceTier.tier_name, targetTier.tier_name),
                        port: null
                    });
                }
            }
        }

        return {
            service_name: serviceMap.name || serviceMap.service_id,
            discovered_at: discoveredAt,
            tiers: (serviceMap.tiers || []).map((tier: any) => ({
                tier_name: tier.tier_name,
                tier_level: tier.tier_level,
                devices: (tier.devices || []).map((device: any) => ({
                    device_id: device.device_id,
                    hostname: device.hostname,
                    ip_address: device.ip_address,
                    role: device.role,
                    config_files: device.config_files || []
                }))
            })),
            connections: connections,
            total_devices: totalDevices,
            discovery_logs: [] // Logs not stored in vector_db format
        };
    }

    /**
     * Infer connection type based on tier names
     */
    private inferConnectionType(sourceTier: string, targetTier: string): string {
        const source = sourceTier.toLowerCase();
        const target = targetTier.toLowerCase();

        if (source.includes('web') || source.includes('load')) {
            return 'routes_to';
        } else if (target.includes('database') || target.includes('db')) {
            return 'connects_to';
        } else if (target.includes('integration') || target.includes('external')) {
            return 'depends_on';
        }
        return 'connects_to';
    }

    /**
     * Merge blockchain and local service maps (blockchain is source of truth)
     * @param blockchainMaps - Maps from blockchain
     * @param localMaps - Maps from localStorage
     * @returns Merged array with blockchain as priority
     */
    private mergeServiceMaps(blockchainMaps: ServiceMapData[], localMaps: ServiceMapData[]): ServiceMapData[] {
        const merged = [...blockchainMaps];

        // Add local-only maps (not on blockchain)
        for (const localMap of localMaps) {
            if (!merged.find(m => m.service_name === localMap.service_name)) {
                merged.push(localMap);
            }
        }

        // Sort by discovered_at (newest first)
        merged.sort((a, b) => Number(b.discovered_at) - Number(a.discovered_at));

        return merged;
    }

    /**
     * View discovery logs for a service map
     * @param serviceName - Name of the service map
     */
    async viewServiceMapLogs(serviceName: string): Promise<void> {
        try {
            const canisterClient = (window as any).canisterClient;
            if (!canisterClient) {
                console.error('❌ CanisterClient not available');
                return;
            }

            const result = await canisterClient.callCanister('service_mapper', 'get_service_map_logs', [serviceName]);

            console.log('📊 get_service_map_logs result:', result);

            let logs: string[] = [];
            if (result && Array.isArray(result)) {
                // Direct array response
                logs = result;
            } else if (result && result.data && Array.isArray(result.data)) {
                // Wrapped in data field (from proxy)
                logs = result.data;
            }

            console.log('📊 Parsed logs:', logs.length, 'entries');

            // Create modal to display logs
            this.showLogsModal(serviceName, logs);

        } catch (error) {
            console.error('❌ Failed to load logs:', error);
            if (typeof (window as any).showToastNotification === 'function') {
                (window as any).showToastNotification('Failed to load discovery logs', 'error');
            }
        }
    }

    /**
     * Show discovery logs in a modal
     * @param serviceName - Service name
     * @param logs - Array of log messages
     */
    private showLogsModal(serviceName: string, logs: string[]): void {
        const modalHtml = `
            <div id="service-logs-modal" class="modal">
                <div class="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
                    <!-- Header -->
                    <div class="flex justify-between items-center p-6 border-b">
                        <h2 class="text-2xl font-bold text-gray-900">Discovery Logs: ${serviceName}</h2>
                        <button onclick="document.getElementById('service-logs-modal').remove()" class="text-gray-500 hover:text-gray-700">
                            <i data-feather="x" class="w-6 h-6"></i>
                        </button>
                    </div>

                    <!-- Logs Content -->
                    <div class="flex-1 overflow-auto p-6">
                        ${logs.length === 0
                            ? '<p class="text-gray-500">No discovery logs available for this service map.</p>'
                            : `<div class="bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-sm overflow-auto">
                                ${logs.map((log, _index) => `<div class="log-entry mb-1">${this.escapeHtml(this.decodeUnicodeEscapes(log))}</div>`).join('')}
                               </div>`
                        }
                    </div>

                    <!-- Footer -->
                    <div class="p-6 border-t">
                        <button onclick="document.getElementById('service-logs-modal').remove()" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                            Close
                        </button>
                    </div>
                </div>
            </div>
        `;

        // Add modal to DOM
        const modalContainer = document.createElement('div');
        modalContainer.innerHTML = modalHtml;
        document.body.appendChild(modalContainer.firstElementChild!);

        // Replace icons
        safeFeatherReplace();
    }

    /**
     * Escape HTML to prevent XSS
     * @param text - Text to escape
     * @returns Escaped HTML string
     */
    private escapeHtml(text: string): string {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    /**
     * Decode Rust-style unicode escapes (\u{xxxx}) to actual unicode characters
     * @param text - Text with potential unicode escapes
     * @returns Text with escapes decoded to actual characters
     */
    private decodeUnicodeEscapes(text: string): string {
        // Replace Rust-style unicode escapes: \u{fe0f} → actual character
        return text.replace(/\\u\{([0-9a-fA-F]+)\}/g, (_match, hex) => {
            return String.fromCodePoint(parseInt(hex, 16));
        });
    }

    /**
     * Load a stored service map and display it
     * Checks localStorage first, then falls back to vector_db
     * @param serviceName - Name of the service map to load
     */
    async loadServiceMap(serviceName: string): Promise<void> {
        // First check localStorage
        const stored = this.getStoredServiceMaps();
        let serviceMap = stored.find(sm => sm.service_name === serviceName);

        // If not in localStorage, try to fetch from vector_db
        if (!serviceMap) {
            console.log('📡 Service map not in cache, fetching from vector_db:', serviceName);
            try {
                const canisterClient = (window as any).canisterClient;
                if (canisterClient) {
                    const result = await canisterClient.callCanister('vector_db', 'get_service_map', [serviceName]);
                    const actualMap = result?.data?.[0] || result?.data || result?.[0] || result;
                    if (actualMap && actualMap.tiers) {
                        serviceMap = this.fromVectorDbFormat(actualMap);
                        console.log('✅ Loaded service map from vector_db:', serviceName);
                    }
                }
            } catch (error) {
                console.error('❌ Failed to fetch from vector_db:', error);
            }
        }

        if (!serviceMap) {
            console.error('❌ Service map not found:', serviceName);
            if (typeof (window as any).showToast === 'function') {
                (window as any).showToast(`Service map "${serviceName}" not found`, 'error');
            }
            return;
        }

        console.log('📂 Loading service map:', serviceName);

        // Set as current service map
        this.currentServiceMap = serviceMap;

        // Update modal title with the service name
        const modalTitle = document.getElementById('service-map-modal-title');
        if (modalTitle) {
            modalTitle.textContent = `${serviceMap.service_name} Service Map`;
        }

        // Open the service map modal FIRST (so #cy container exists)
        const modal = document.getElementById('service-map-modal');
        if (modal) {
            modal.classList.remove('hidden');
        }

        // Use setTimeout to ensure DOM has updated after modal display
        setTimeout(() => {
            // Initialize visualizer if needed (now that modal is visible)
            if (!this.visualizer) {
                console.log('🎨 Creating ServiceMapVisualizer instance...');
                this.visualizer = new ServiceMapVisualizer('cy');
                this.visualizer.initialize();
            }

            // Render the service map
            this.visualizer.renderServiceMap(serviceMap);
        }, 50);

        // Update stats
        const elements = {
            nodes: document.getElementById('stat-nodes'),
            edges: document.getElementById('stat-edges'),
            time: document.getElementById('stat-time'),
            tiers: document.getElementById('stat-tiers')
        };

        if (elements.nodes) elements.nodes.textContent = String(serviceMap.total_devices);
        if (elements.edges) elements.edges.textContent = String(serviceMap.connections.length);
        if (elements.time) elements.time.textContent = 'N/A';
        if (elements.tiers) elements.tiers.textContent = String(serviceMap.tiers.length);

        // Show stats
        const statsDiv = document.getElementById('service-map-stats');
        if (statsDiv) statsDiv.classList.remove('hidden');
    }

    /**
     * Initialize the controller and create visualizer
     * @param containerElementId - ID of the Cytoscape container
     * @returns This controller instance for chaining
     */
    initialize(containerElementId: string): ServiceMapController {
        if (!this.visualizer) {
            console.log('🎨 Creating ServiceMapVisualizer instance...');
            this.visualizer = new ServiceMapVisualizer(containerElementId);
            this.visualizer.initialize();
        }
        return this;
    }

    /**
     * Run service discovery and visualization workflow
     * Orchestrates the entire process from discovery to rendering
     * @param serviceName - Name of the service to discover
     * @param uiElements - Object containing UI element references
     */
    async runDiscovery(serviceName: string, uiElements: UIElements): Promise<void> {
        const {
            statusBar,
            statusText,
            progressText,
            progressBar,
            runBtn,
            statsDiv
        } = uiElements;

        console.log(`🔍 Starting service discovery for: ${serviceName}`);

        // Disable run button
        this.setButtonState(runBtn, true, 'Discovering...');

        // Show status bar
        if (statusBar) statusBar.classList.remove('hidden');
        this.updateProgress(progressBar, statusText, progressText, 30,
            `Generating service map for "${serviceName}"...`,
            'Calling ii_agent canister...');

        const startTime = Date.now();

        try {
            // Step 1: Generate service map from ii_agent
            console.log('📞 Calling window.iiAgent.generateServiceMap...');
            const serviceMap = await this.iiAgent.generateServiceMap(serviceName);
            console.log('✅ Service map received:', serviceMap);

            this.currentServiceMap = serviceMap;

            // Step 2: Initialize visualizer if needed
            this.updateProgress(progressBar, statusText, progressText, 60,
                'Service map generated!',
                'Initializing interactive visualizer...');

            await this.sleep(500);

            if (!this.visualizer) {
                throw new Error('Visualizer not initialized. Call initialize() first.');
            }

            // Step 3: Render service map
            this.updateProgress(progressBar, statusText, progressText, 80,
                'Rendering service topology...',
                `Rendering ${serviceMap.total_devices} devices with ${serviceMap.connections.length} connections...`);

            await this.sleep(500);

            this.visualizer.renderServiceMap(serviceMap);

            // Step 4: Store service map
            this.storeServiceMap(serviceMap);

            // Step 5: Update stats panel
            const elapsedTime = ((Date.now() - startTime) / 1000).toFixed(1);
            this.updateStats({
                nodes: serviceMap.total_devices || 0,
                edges: serviceMap.connections.length || 0,
                time: `${elapsedTime}s`,
                tiers: serviceMap.tiers.length || 0
            });

            if (statsDiv) statsDiv.classList.remove('hidden');

            // Step 5: Complete
            this.updateProgress(progressBar, statusText, progressText, 100,
                'Service discovery completed!',
                `Mapped ${serviceMap.total_devices} devices across ${serviceMap.tiers.length} tiers in ${elapsedTime}s`);

            // Hide status bar after 2 seconds
            await this.sleep(2000);
            if (statusBar) statusBar.classList.add('hidden');

            // Show success notification
            if (typeof (window as any).showToastNotification === 'function') {
                (window as any).showToastNotification('Service topology mapped successfully!', 'success');
            }

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            console.error('❌ Service discovery failed:', error);
            this.updateProgress(progressBar, statusText, progressText, 0,
                'Discovery failed!',
                errorMessage);

            if (typeof (window as any).showToastNotification === 'function') {
                (window as any).showToastNotification('Service discovery failed: ' + errorMessage, 'error');
            }
        } finally {
            // Re-enable run button
            this.setButtonState(runBtn, false, 'Re-run Discovery');
        }
    }

    /**
     * Run service discovery with live step-by-step visualization
     * Shows real-time log and incremental graph building
     * @param serviceName - Name of the service to discover
     * @param uiElements - Object containing UI element references
     */
    async runDiscoveryLive(serviceName: string, uiElements: UIElements): Promise<void> {
        const {
            statusBar,
            statusText,
            progressText,
            progressBar,
            runBtn,
            statsDiv
        } = uiElements;

        console.log(`🔍 Starting LIVE service discovery for: ${serviceName}`);

        // Disable run button
        this.setButtonState(runBtn, true, 'Discovering...');

        // Show status bar and live log
        if (statusBar) statusBar.classList.remove('hidden');
        const liveLog = document.getElementById('live-discovery-log');
        if (liveLog) liveLog.style.display = 'block';

        const logContainer = document.getElementById('log-container');
        if (logContainer) logContainer.innerHTML = ''; // Clear previous logs

        this.updateProgress(progressBar, statusText, progressText, 10,
            `Initializing live service discovery...`,
            'Calling ii_agent.generateServiceMapLive...');

        const startTime = Date.now();

        try {
            // Step 1: Call generate_service_map_live
            console.log('📞 Calling window.iiAgent.generateServiceMapLive...');
            const response = await this.iiAgent.generateServiceMapLive(serviceName);
            console.log('✅ Service map generation response received:', response);

            // Initialize visualizer if needed
            if (!this.visualizer) {
                throw new Error('Visualizer not initialized. Call initialize() first.');
            }

            // Clear graph before starting incremental build
            this.visualizer.clearGraph();

            // Step 2: Process each generation step incrementally
            const totalSteps = response.generation_steps.length;
            console.log(`📝 Processing ${totalSteps} generation steps with incremental graph building...`);

            for (let i = 0; i < totalSteps; i++) {
                const step = response.generation_steps[i];

                // Update progress
                const progress = 20 + (i / totalSteps) * 75; // 20-95%
                this.updateProgress(progressBar, statusText, progressText, progress,
                    'Discovering service topology...',
                    `Processing step ${i + 1} of ${totalSteps}...`);

                // Add log entry
                this.addLogEntry(step);

                // Apply graph update if present (synchronized with log!)
                if (step.graph_update) {
                    await this.applyGraphUpdate(step.graph_update);
                }

                // Delay for animation (500ms per step for readable scrolling - slowed down by half)
                await this.sleep(500);
            }

            // Step 3: Store final service map
            this.currentServiceMap = response.final_service_map;

            // Step 3.5: Fallback - if graph is empty, render final map
            // (This handles case where backend didn't emit graph_update objects)
            const nodeCount = this.visualizer.getNodeCount();
            if (nodeCount === 0 && response.final_service_map.total_devices > 0) {
                console.log('⚠️ No graph updates detected, rendering final map as fallback...');
                this.updateProgress(progressBar, statusText, progressText, 95,
                    'Rendering final service map...',
                    'Applying fallback rendering...');
                await this.sleep(200);
                this.visualizer.renderServiceMap(response.final_service_map);
            }

            // Step 3.75: Store service map
            // IMPORTANT: Populate discovery_logs from generation_steps before saving
            // Backend leaves discovery_logs empty (uses generation_steps instead)
            response.final_service_map.discovery_logs = response.generation_steps.map(step => {
                // Decode unicode escape sequences like \u{fe0f} to actual emojis
                let logText = step.log_message;
                // Replace unicode escape sequences \u{XXXX}
                logText = logText.replace(/\\u\{([0-9a-fA-F]+)\}/g, (_match, hex) => {
                    return String.fromCodePoint(parseInt(hex, 16));
                });
                // Replace other escape sequences
                logText = logText.replace(/\\n/g, '\n').replace(/\\t/g, '\t').replace(/\\\\/g, '\\');
                return logText;
            });
            console.log(`📝 Populated ${response.final_service_map.discovery_logs.length} discovery logs from generation steps`);

            this.storeServiceMap(response.final_service_map);

            // Step 4: Update stats panel
            const elapsedTime = ((Date.now() - startTime) / 1000).toFixed(1);
            this.updateStats({
                nodes: response.final_service_map.total_devices || 0,
                edges: response.final_service_map.connections.length || 0,
                time: `${elapsedTime}s`,
                tiers: response.final_service_map.tiers.length || 0
            });

            if (statsDiv) statsDiv.classList.remove('hidden');

            // Step 5: Complete
            this.updateProgress(progressBar, statusText, progressText, 100,
                'Live service discovery completed!',
                `Mapped ${response.final_service_map.total_devices} devices in ${elapsedTime}s`);

            // Hide status bar after 2 seconds
            await this.sleep(2000);
            if (statusBar) statusBar.classList.add('hidden');

            // Show success notification
            if (typeof (window as any).showToastNotification === 'function') {
                (window as any).showToastNotification('Service topology mapped successfully!', 'success');
            }

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            console.error('❌ Live service discovery failed:', error);
            this.updateProgress(progressBar, statusText, progressText, 0,
                'Discovery failed!',
                errorMessage);

            if (typeof (window as any).showToastNotification === 'function') {
                (window as any).showToastNotification('Service discovery failed: ' + errorMessage, 'error');
            }
        } finally {
            // Re-enable run button
            this.setButtonState(runBtn, false, 'Re-run Discovery');
        }
    }

    /**
     * Apply a graph update (add node or edge) to the visualizer
     * This creates the synchronized effect between log and graph
     * @param update - Graph update to apply
     */
    private async applyGraphUpdate(update: GraphUpdate): Promise<void> {
        if (!this.visualizer) return;

        if (update.action === 'add_node' && update.node_data) {
            this.visualizer.addNode(update.node_data);
        } else if (update.action === 'add_edge' && update.edge_data) {
            this.visualizer.addEdge(update.edge_data);
        }

        // Small delay to allow graph animation to complete
        await this.sleep(20);
    }

    /**
     * Add a log entry to the live log display
     * @param step - Service map generation step with log message
     */
    private addLogEntry(step: ServiceMapGenerationStep): void {
        const logContainer = document.getElementById('log-container');
        if (!logContainer) return;

        const logEntry = document.createElement('div');
        logEntry.className = 'log-entry mb-1';

        // Determine color based on log level
        let colorClass = 'text-gray-300';
        if (step.log_level === 'success') colorClass = 'text-green-400';
        else if (step.log_level === 'error') colorClass = 'text-red-400';
        else if (step.log_level === 'warning') colorClass = 'text-yellow-400';

        logEntry.innerHTML = `<span class="text-gray-500">${step.timestamp}</span> <span class="${colorClass}">${this.escapeHtml(this.decodeUnicodeEscapes(step.log_message))}</span>`;

        logContainer.appendChild(logEntry);

        // Auto-scroll to bottom
        logContainer.scrollTop = logContainer.scrollHeight;
    }

    /**
     * Update progress indicators in UI
     * @param progressBar - Progress bar element
     * @param statusText - Status text element
     * @param progressText - Progress details text element
     * @param percentage - Progress percentage (0-100)
     * @param status - Status message
     * @param progress - Detailed progress message
     */
    private updateProgress(
        progressBar: HTMLElement | null,
        statusText: HTMLElement | null,
        progressText: HTMLElement | null,
        percentage: number,
        status: string,
        progress: string
    ): void {
        if (progressBar) progressBar.style.width = `${percentage}%`;
        if (statusText) statusText.textContent = status;
        if (progressText) progressText.textContent = progress;
    }

    /**
     * Update stats panel with discovery results
     * @param stats - Statistics to display
     */
    private updateStats(stats: ServiceMapStats): void {
        const elements = {
            nodes: document.getElementById('stat-nodes'),
            edges: document.getElementById('stat-edges'),
            time: document.getElementById('stat-time'),
            tiers: document.getElementById('stat-tiers')
        };

        if (elements.nodes) elements.nodes.textContent = String(stats.nodes);
        if (elements.edges) elements.edges.textContent = String(stats.edges);
        if (elements.time) elements.time.textContent = stats.time;
        if (elements.tiers) elements.tiers.textContent = String(stats.tiers);
    }

    /**
     * Set button state and update UI
     * @param button - Button element to update
     * @param disabled - Whether button should be disabled
     * @param text - Button text
     */
    private setButtonState(button: HTMLButtonElement | null, disabled: boolean, text: string): void {
        if (!button) return;

        button.disabled = disabled;
        const icon = disabled
            ? '<i data-feather="loader" class="animate-spin"></i>'
            : '<i data-feather="refresh-cw"></i>';
        button.innerHTML = `${icon} ${text}`;

        // Refresh feather icons
        safeFeatherReplace();
    }

    /**
     * Reset visualizer view to default
     */
    resetView(): void {
        if (this.visualizer) {
            this.visualizer.resetView();
        }
    }

    /**
     * Export service map as PNG image
     */
    exportImage(): void {
        if (this.visualizer) {
            this.visualizer.exportImage();
        } else {
            alert('No service map to export. Please run discovery first.');
        }
    }

    /**
     * Get current service map data
     * @returns Current service map or null
     */
    getCurrentServiceMap(): ServiceMapData | null {
        return this.currentServiceMap;
    }

    /**
     * Sleep utility for async delays
     * @param ms - Milliseconds to sleep
     * @returns Promise that resolves after delay
     */
    private sleep(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}

// Export to global scope
declare global {
    interface Window {
        ServiceMapController: typeof ServiceMapController;
        serviceMapController?: ServiceMapController;
    }
}

window.ServiceMapController = ServiceMapController;

